import React from "react";
import { CirclePlay, CircleStop } from "lucide-react";

import { useAuth } from "../../../features/auth/AuthContext";
import {
    downloadUserProcessLog,
    fetchUserProcessLog,
    ProcessLogComparisonSite,
    ProcessLogPagination,
    ProcessLogQueryRange,
    ProcessLogType,
} from "../../../api/users";
import { fetchShopList } from "../../shops/shops.api";
import * as helpers from "../../../shared/lib/helpers";
import { getErrorMessage } from "../../../shared/api/helpers";
import { toast } from "../../../shared/ui/toast";
import { getProcessResultDescription } from "../../../shared/lib/helpers";
import { getKstDateString } from "../process-records.utils";

const PROCESS_LOG_PAGE_SIZE = 100;

interface Shop {
    id: string;
    name: string;
    market_site: string;
}

interface ProcessLog {
    shop_item_gid: string;
    finished_at: string;
    compare_url?: string;
    shop_name: string;
    shop_item_id: string;
    item_name: string;
    price_lowest: number;
    target_rank: number;
    adjust_amount: number;
    price: number;
    price_after_limit?: number;
    price_after_adjust?: number;
    discount: number;
    discount_after_limit?: number;
    discount_after_adjust?: number;
    adjust_strategy?: string;
    price_decision_reason?: string;
    message?: string;
    is_success: boolean;
    error_message?: string;
    adjust_amount_after_limit?: number;
}

const formatNumber = (value: number | null | undefined) => (
    typeof value === "number" ? helpers.formatNumber(value) : "-"
);

const ProcessRecordListPage = ({ itemId }: { itemId?: string }) => {
    const auth = useAuth();
    const [logs, setLogs] = React.useState<ProcessLog[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [historyError, setHistoryError] = React.useState("");
    const [shops, setShops] = React.useState<Shop[]>([]);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pagination, setPagination] = React.useState<ProcessLogPagination>({
        count: 0,
        next: null,
        previous: null,
    });

    // 필터 상태
    const [selectedDate, setSelectedDate] = React.useState<string>(
        getKstDateString()
    );
    const [selectedProcessType, setSelectedProcessType] = React.useState<ProcessLogType>(itemId ? 'item' : 'all');
    const [selectedComparisonSite, setSelectedComparisonSite] = React.useState<ProcessLogComparisonSite>('all');
    const [selectedShop, setSelectedShop] = React.useState<string>('');
    const [searchRange, setSearchRange] = React.useState<ProcessLogQueryRange>('all');
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [appliedSearchQuery, setAppliedSearchQuery] = React.useState<string>('');


    // 날짜 옵션 생성 (오늘부터 과거 30일)
    const getDateOptions = () => {
        const options = [];
        for (let i = 0; i <= 30; i++) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateStr = getKstDateString(date);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const weekday = date.toLocaleString('ko-KR', { weekday: 'short' });
            const label = `${year}년 ${month}월 ${day}일(${weekday})`;
            options.push({ value: dateStr, label });
        }
        return options;
    };

    const dateOptions = React.useMemo(() => getDateOptions(), []);

    // 날짜 옵션 값 배열 생성
    const availableDates = React.useMemo(() => dateOptions.map(opt => opt.value), [dateOptions]);

    // 현재 선택된 날짜의 인덱스
    const currentDateIndex = React.useMemo(() => {
        return availableDates.indexOf(selectedDate);
    }, [selectedDate, availableDates]);

    const totalPages = Math.max(1, Math.ceil(pagination.count / PROCESS_LOG_PAGE_SIZE));
    const isDetailSearchEnabled = selectedProcessType !== 'running';
    const hasDetailSearchFilter = selectedComparisonSite !== 'all'
        || Boolean(selectedShop)
        || Boolean(appliedSearchQuery.trim());
    const isProductProcessView = Boolean(itemId) || selectedProcessType === 'item'
        || (selectedProcessType === 'all' && hasDetailSearchFilter);
    const isStateProcessView = selectedProcessType === 'running';

    // 데이터 로드
    React.useEffect(() => {
        const loadData = async () => {
            if (itemId) {
                setIsLoading(true);
                setHistoryError("");
            }
            try {
                await fetchUserProcessLog({
                    auth: auth,
                    itemId,
                    dateStr: itemId ? undefined : selectedDate,
                    processType: itemId ? 'item' : selectedProcessType,
                    shopId: itemId ? undefined : selectedShop || undefined,
                    comparisonSite: itemId ? undefined : selectedComparisonSite,
                    query: itemId ? undefined : appliedSearchQuery,
                    queryRange: itemId ? undefined : searchRange,
                    page: currentPage,
                    setErrorMessage: (message) => { if (itemId) setHistoryError(message); },
                    setResult: setLogs,
                    setPagination,
                });
            } catch (error) {
                console.error("Error fetching process logs:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [itemId, selectedDate, selectedProcessType, selectedShop, selectedComparisonSite, searchRange, appliedSearchQuery, currentPage]);

    // 상점 목록 로드
    React.useEffect(() => {
        if (itemId) return;
        const loadShops = async () => {
            try {
                await fetchShopList({
                    auth: auth,
                    setErrorMessage: () => { },
                    setResult: (shopsData: any) => {
                        setShops(shopsData);
                    },
                });
            } catch (error) {
                console.error("Error fetching shops:", error);
            }
        };
        loadShops();
    }, [itemId]);

    // 날짜 변경 함수
    const handleDateChange = (offset: number) => {
        if (offset === 0) {
            // 오늘 버튼
            setSelectedDate(getKstDateString());
        } else {
            // 이전/다음 버튼: dateOptions 배열의 인덱스로 이동
            const newIndex = currentDateIndex + offset;
            if (newIndex >= 0 && newIndex < dateOptions.length) {
                setSelectedDate(dateOptions[newIndex].value);
            }
        }
        setCurrentPage(1);
    };

    // 검색 실행
    const handleSearch = () => {
        setAppliedSearchQuery(searchQuery);
        setCurrentPage(1);
    };

    // 초기화
    const handleReset = () => {
        setSelectedDate(getKstDateString());
        setSelectedProcessType('all');
        setSelectedComparisonSite('all');
        setSelectedShop('');
        setSearchRange('all');
        setSearchQuery('');
        setAppliedSearchQuery('');
        setCurrentPage(1);
    };

    React.useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const response = await downloadUserProcessLog({
                auth,
                dateStr: selectedDate,
                processType: selectedProcessType,
                shopId: selectedShop || undefined,
                comparisonSite: selectedComparisonSite,
                query: appliedSearchQuery,
                queryRange: searchRange,
            });
            const contentDisposition = response.headers["content-disposition"] || "";
            const encodedFilename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
            const plainFilename = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];
            const filename = encodedFilename
                ? decodeURIComponent(encodedFilename)
                : plainFilename || `상품작업이력_${selectedDate}.xlsx`;
            const objectUrl = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
            toast.success("엑셀 파일을 다운로드했습니다.");
        } catch (error: any) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsDownloading(false);
        }
    };

    return <div className={`process-record-page${itemId ? " item-process-records" : ""}`}>
        {/* 타이틀 */}
        {!itemId && <>
        <div className="page-head page-head-standard d-flex justify-content-between align-items-center">
            <div className="d-flex">
                <p className="fs-5 me-2">
                    상품 작업 이력
                </p>
            </div>
            <div className="d-flex">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleDownload}
                    disabled={isDownloading}
                >
                    {isDownloading ? "다운로드 중..." : "엑셀 다운로드"}
                </button>
            </div>
        </div>
        </>}

        {!itemId && <>
        <div className="process-record-filter-toolbar mb-3">
            <div className="process-record-filter-group">
                <span className="process-record-filter-label">조회일</span>
                <div className="process-record-filter-controls">
                <select
                    className="form-select process-record-date-select"
                    aria-label="조회일"
                    value={selectedDate}
                    onChange={(e) => {
                        setSelectedDate(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    {dateOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={() => handleDateChange(0)}
                >
                    오늘
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={() => handleDateChange(1)}
                    disabled={currentDateIndex >= dateOptions.length - 1}
                >
                    이전
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={() => handleDateChange(-1)}
                    disabled={currentDateIndex <= 0}
                >
                    다음
                </button>
                </div>
            </div>
            <div className="process-record-filter-group process-record-separated-group">
                <span className="process-record-filter-label">작업유형</span>
                <div className="process-record-filter-controls">
                <select
                    className="form-select process-record-type-select"
                    aria-label="작업유형"
                    value={selectedProcessType}
                    onChange={(e) => {
                        setSelectedProcessType(e.target.value as ProcessLogType);
                        setCurrentPage(1);
                    }}
                >
                    <option value="all">전체</option>
                    <option value="item">상품</option>
                    <option value="running">실행/정지</option>
                </select>
                </div>
            </div>
            <div className="process-record-filter-group process-record-separated-group process-record-search-group">
                <span className="process-record-filter-label">상세 검색</span>
                <div className="process-record-filter-controls">
                <select
                    className="form-select process-record-site-select"
                    aria-label="비교사이트"
                    value={selectedComparisonSite}
                    disabled={!isDetailSearchEnabled}
                    onChange={(e) => {
                        setSelectedComparisonSite(e.target.value as ProcessLogComparisonSite);
                        setCurrentPage(1);
                    }}
                >
                    <option value="all">비교사이트 전체</option>
                    <option value="danawa">다나와</option>
                    <option value="enuri">에누리</option>
                    <option value="naver">네이버</option>
                    <option value="coupang" disabled>쿠팡 (준비중)</option>
                </select>
                <select
                    className="form-select process-record-shop-select"
                    aria-label="상점"
                    value={selectedShop}
                    disabled={!isDetailSearchEnabled}
                    onChange={(e) => {
                        setSelectedShop(e.target.value);
                        setCurrentPage(1);
                    }}
                >
                    <option value="">상점 전체</option>
                    {shops.map(shop => (
                        <option key={shop.id} value={shop.id}>
                            {shop.name}
                        </option>
                    ))}
                </select>
                <select
                    className="form-select process-record-range-select"
                    aria-label="검색 대상"
                    value={searchRange}
                    disabled={!isDetailSearchEnabled}
                    onChange={(e) => {
                        setSearchRange(e.target.value as ProcessLogQueryRange);
                        setCurrentPage(1);
                    }}
                >
                    <option value="all">전체 항목</option>
                    <option value="item_name">상품명</option>
                    <option value="shop_item_id">상품코드</option>
                </select>
                <input
                    type="text"
                    className="form-control process-record-search-query"
                    placeholder="검색어 입력"
                    aria-label="검색어"
                    value={searchQuery}
                    disabled={!isDetailSearchEnabled}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={handleSearch}
                    disabled={!isDetailSearchEnabled}
                >
                    검색
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    onClick={handleReset}
                >
                    초기화
                </button>
                </div>
            </div>
        </div>
        </>}

        {historyError && <div className="alert alert-danger" role="alert">{historyError}</div>}

        <div className="data-table-card process-record-table-card">
            <div className="table-scroll">
            <table className="table process-record-table">
                <thead>
                    {isStateProcessView ? (
                    <tr>
                        <th>일시</th>
                        <th>작업유형</th>
                        <th>메시지</th>
                    </tr>
                    ) : !isProductProcessView ? (
                    <tr>
                        <th>일시</th>
                        <th>작업유형</th>
                        <th>비교</th>
                        <th>상점</th>
                        <th>코드</th>
                        <th>품명</th>
                        <th className="text-end">하한</th>
                        <th className="text-end">순위</th>
                        <th className="text-end">조정</th>
                        <th className="text-end">전 금액</th>
                        <th className="text-end">후 금액</th>
                        <th className="text-end">전 할인</th>
                        <th className="text-end">후 할인</th>
                        <th>처리상태</th>
                        <th>처리결과</th>
                    </tr>
                    ) : (
                    <tr>
                        <th>일시</th>
                        <th>비교</th>
                        <th>상점</th>
                        <th>코드</th>
                        <th>품명</th>
                        <th className="text-end">하한</th>
                        <th className="text-end">순위</th>
                        <th className="text-end">조정</th>
                        <th className="text-end">전 금액</th>
                        <th className="text-end">후 금액</th>
                        <th className="text-end">전 할인</th>
                        <th className="text-end">후 할인</th>
                        <th>처리상태</th>
                        <th>처리결과</th>
                    </tr>
                    )}
                </thead>
                <tbody>
                    {logs.length === 0 && <tr><td colSpan={isStateProcessView ? 3 : isProductProcessView ? 14 : 15} className="text-center">{isLoading ? '작업 이력을 불러오는 중입니다.' : '작업 이력이 없습니다.'}</td></tr>}
                    {logs.length > 0 && logs.map((log: any, index: number) => (
                        isStateProcessView ? (
                        <tr key={`${log.finished_at}-${log.id || index}`}>
                            <td>{new Date(log.finished_at).toLocaleString('ja-JP', {
                                timeZone: 'Asia/Seoul',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            })}</td>
                            <td><span className="d-inline-flex align-items-center gap-1">
                                {log.category === 'is_running_true'
                                    ? <CirclePlay className="text-success" size={20} />
                                    : <CircleStop className="text-muted" size={20} />}
                                {log.category === 'is_running_true' ? '실행' : '정지'}
                            </span></td>
                            <td>{log.message || '-'}</td>
                        </tr>
                        ) : !isProductProcessView ? (
                        <tr key={`${log.finished_at}-${log.shop_item_gid || log.id || index}`}>
                            <td>{new Date(log.finished_at).toLocaleString('ja-JP', {
                                timeZone: 'Asia/Seoul',
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                            })}</td>
                            <td>{log.category === 'is_running_true' || log.category === 'is_running_false' ? '실행/정지' : '상품'}</td>
                            {log.category === 'is_running_true' || log.category === 'is_running_false' ? (
                            <td colSpan={13}>
                                {log.category === 'is_running_true'
                                    ? <CirclePlay className="text-success me-1" size={20} />
                                    : <CircleStop className="text-muted me-1" size={20} />}
                                {log.message || '-'}
                            </td>
                            ) : (
                            <>
                                <td>
                                    {log.compare_url && log.compare_url.indexOf('danawa') !== -1 ? '다나와 ' : ''}
                                    {log.compare_url && log.compare_url.indexOf('enuri') !== -1 ? '에누리 ' : ''}
                                    {log.compare_url && log.compare_url.indexOf('naver') !== -1 ? '네이버 ' : ''}
                                </td>
                                <td>{log.shop_name}</td>
                                <td>{log.shop_item_id}</td>
                                <td>{log.item_name}</td>
                                <td className="text-end">{formatNumber(log.price_lowest)}</td>
                                <td className="text-end">{formatNumber(log.target_rank)}</td>
                                <td className="text-end">{formatNumber(log.adjust_amount)}</td>
                                <td className="text-end">{formatNumber(log.price)}</td>
                                <td className="text-end">{formatNumber(log.price_after_limit ?? log.price_after_adjust)}</td>
                                <td className="text-end">{formatNumber(log.discount)}</td>
                                <td className="text-end">{formatNumber(log.discount_after_limit ?? log.discount_after_adjust)}</td>
                                <td>{log.is_success ? "성공" : "실패"}</td>
                                <td>
                                    {log.is_success ? (
                                        getProcessResultDescription(log)
                                    ) : (
                                        (log.message || log.error_message) && (
                                            log.message || log.error_message
                                        )
                                    )}
                                </td>
                            </>
                            )}
                        </tr>
                        ) : (
                        <tr key={`${log.finished_at}-${log.shop_item_gid}-${index}`}>
                            <td>
                                {/* {new Date(log.created_at).toLocaleString('ko-KR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                })} */}
                                {new Date(log.finished_at).toLocaleString('ja-JP', {
                                    timeZone: 'Asia/Seoul',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                })}
                            </td>
                            <td>
                                {log.compare_url && log.compare_url.indexOf('danawa') !== -1 ? '다나와 ' : ''}
                                {log.compare_url && log.compare_url.indexOf('enuri') !== -1 ? '에누리 ' : ''}
                                {log.compare_url && log.compare_url.indexOf('naver') !== -1 ? '네이버 ' : ''}
                            </td>
                            <td>{log.shop_name}</td>
                            <td>{log.shop_item_id}</td>
                            <td>{log.item_name}</td>
                            <td className="text-end">{formatNumber(log.price_lowest)}</td>
                            <td className="text-end">{formatNumber(log.target_rank)}</td>
                            <td className="text-end">{formatNumber(log.adjust_amount)}</td>
                            <td className="text-end">{formatNumber(log.price)}</td>
                            <td className="text-end">{formatNumber(log.price_after_limit ?? log.price_after_adjust)}</td>
                            <td className="text-end">{formatNumber(log.discount)}</td>
                            <td className="text-end">{formatNumber(log.discount_after_limit ?? log.discount_after_adjust)}</td>
                            <td>{log.is_success ? "성공" : "실패"}</td>
                            <td>
                                {log.is_success ? (
                                    getProcessResultDescription(log)
                                ) : (
                                    (log.message || log.error_message) && (
                                        log.message || log.error_message
                                    )
                                )}
                            </td>
                        </tr>
                        )
                    ))}
                    {/* <tr>
                        <td>2025-10-27 13:57:57</td>
                        <td>다나와</td>
                        <td>옥션2</td>
                        <td>E891448578</td>
                        <td>식물나라 뽀얀쌀 생기튼튼</td>
                        <td>12300</td>
                        <td>1</td>
                        <td>-10</td>
                        <td>0</td>
                        <td>0</td>
                        <td></td>
                        <td></td>
                        <td>순위결정불가(전체)</td>
                    </tr>
                    <tr>
                        <td>2025-10-27 13:57:57</td>
                        <td>다나와</td>
                        <td>옥션2</td>
                        <td>E891448578</td>
                        <td>식물나라 뽀얀쌀 생기튼튼</td>
                        <td>12300</td>
                        <td>1</td>
                        <td>-10</td>
                        <td>0</td>
                        <td>0</td>
                        <td></td>
                        <td></td>
                        <td>가격유지</td>
                    </tr>
                    <tr>
                        <td>2025-10-27 13:57:57</td>
                        <td>다나와</td>
                        <td>옥션2</td>
                        <td>E891448578</td>
                        <td>식물나라 뽀얀쌀 생기튼튼</td>
                        <td>12300</td>
                        <td>1</td>
                        <td>-10</td>
                        <td>13080</td>
                        <td>13060</td>
                        <td></td>
                        <td></td>
                        <td>가격수정완료</td>
                    </tr>
                    <tr>
                        <td>2025-10-27 13:57:57</td>
                        <td>다나와</td>
                        <td>옥션2</td>
                        <td>E891448578</td>
                        <td>식물나라 뽀얀쌀 생기튼튼</td>
                        <td>12300</td>
                        <td>1</td>
                        <td>-10</td>
                        <td>13080</td>
                        <td>13060</td>
                        <td></td>
                        <td></td>
                        <td>가격비교 상품없음</td>
                    </tr>
                    <tr>
                        <td>2025-10-27 13:57:57</td>
                        <td>다나와</td>
                        <td>옥션2</td>
                        <td>E891448578</td>
                        <td>식물나라 뽀얀쌀 생기튼튼</td>
                        <td>12300</td>
                        <td>1</td>
                        <td>-10</td>
                        <td>13070</td>
                        <td>15420</td>
                        <td></td>
                        <td></td>
                        <td>ESMPLUS 가격 수정 중 오류가 발생하였습니다.</td>
                    </tr> */}
                </tbody>
            </table>
            </div>
        </div>
        <div className="process-record-pagination d-flex align-items-center justify-content-between mt-3">
            <span className="text-secondary">
                총 {pagination.count.toLocaleString()}건 · {currentPage} / {totalPages}페이지
            </span>
            <div className="d-flex gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label="이전 페이지"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage <= 1}
                >
                    이전
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    aria-label="다음 페이지"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage >= totalPages}
                >
                    다음
                </button>
            </div>
        </div>
    </div>
}

export default ProcessRecordListPage;
